import React, { useEffect, useRef } from 'react';
import { ScrollReveal } from './ScrollReveal';
import './Process.css';

const steps = [
  {
    tag: '01 / DISCOVER',
    title: 'Map how you actually work',
    desc: 'We study your real operating model — not the org chart version — to find where the friction lives.',
    position: { left: '12%', top: '12%', textAlign: 'left' },
  },
  {
    tag: '02 / DESIGN',
    title: 'Design around your team',
    desc: 'We shape the solution to fit your workflows, not the other way around — flexible from the first sketch.',
    position: { left: '88%', top: '12%', textAlign: 'right' },
  },
  {
    tag: '03 / BUILD',
    title: 'Build the full stack',
    desc: 'End-to-end delivery — one team owns the software, the automation and the integration together.',
    position: { left: '88%', top: '88%', textAlign: 'right' },
  },
  {
    tag: '04 / ADAPT',
    title: 'Keep adapting, on loop',
    desc: 'We stay close after launch, evolving the system as your business changes — the loop never really closes.',
    position: { left: '12%', top: '88%', textAlign: 'left' },
  },
];

function ProcessLoopSVG() {
  const svgRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Upper loop path: M 400 60 C 250 60, 250 240, 400 240 C 550 240, 550 60, 400 60
    // Lower loop path: M 400 260 C 250 260, 250 440, 400 440 C 550 440, 550 260, 400 260

    // Parametric figure-8 through the two oval paths
    function figureEightPoint(t) {
      // t from 0 to 2PI traces the full figure-8
      // Upper loop: t from 0 to PI
      // Lower loop: t from PI to 2PI
      const cx = 400;

      if (t <= Math.PI) {
        // Upper loop (clockwise from top-center)
        const frac = t / Math.PI; // 0 to 1
        const angle = frac * Math.PI * 2; // full ellipse parametric
        const rx = 150; // horizontal radius
        const ryTop = 90;  // vertical radius
        const cyUpper = 150; // center y of upper loop
        return {
          x: cx + rx * Math.sin(angle),
          y: cyUpper - ryTop * Math.cos(angle),
        };
      } else {
        // Lower loop (clockwise from center to bottom and back)
        const frac = (t - Math.PI) / Math.PI; // 0 to 1
        const angle = frac * Math.PI * 2;
        const rx = 150;
        const ryBottom = 90;
        const cyLower = 350;
        return {
          x: cx - rx * Math.sin(angle),
          y: cyLower - ryBottom * Math.cos(angle),
        };
      }
    }

    // Create traveling dots
    const dotCount = 3;
    const dots = [];
    for (let i = 0; i < dotCount; i++) {
      // Main dot
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('r', '5');
      c.setAttribute('fill', '#9b7bff');
      c.style.filter = 'drop-shadow(0 0 8px rgba(155, 123, 255, 0.9)) drop-shadow(0 0 16px rgba(155, 123, 255, 0.5))';
      svg.appendChild(c);

      // Glow halo
      const halo = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      halo.setAttribute('r', '12');
      halo.setAttribute('fill', 'rgba(155, 123, 255, 0.15)');
      svg.appendChild(halo);

      dots.push({
        el: c,
        halo,
        speed: 0.15 + i * 0.05,
        offset: (i / dotCount) * Math.PI * 2,
      });
    }

    // Create a pulsing glow at each node point
    const nodePositions = [
      { x: 400, y: 60 },
      { x: 400, y: 240 },
      { x: 400, y: 260 },
      { x: 400, y: 440 },
    ];

    const nodePulses = nodePositions.map((pos) => {
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('cx', pos.x);
      c.setAttribute('cy', pos.y);
      c.setAttribute('r', '4');
      c.setAttribute('fill', '#9b7bff');
      c.style.filter = 'drop-shadow(0 0 6px rgba(155, 123, 255, 0.9))';
      svg.appendChild(c);
      return c;
    });

    let t0 = 0;
    let animId;

    function render() {
      // Animate traveling dots along figure-8
      dots.forEach((dot) => {
        const t = ((t0 * 0.001 * dot.speed) + dot.offset) % (Math.PI * 2);
        const pt = figureEightPoint(t);
        dot.el.setAttribute('cx', pt.x.toFixed(1));
        dot.el.setAttribute('cy', pt.y.toFixed(1));
        dot.halo.setAttribute('cx', pt.x.toFixed(1));
        dot.halo.setAttribute('cy', pt.y.toFixed(1));

        // Pulse opacity
        const pulse = 0.6 + Math.sin(t0 * 0.003 + dot.offset) * 0.4;
        dot.el.setAttribute('opacity', pulse.toFixed(2));
        dot.halo.setAttribute('opacity', (pulse * 0.4).toFixed(2));
      });

      // Pulse node dots
      nodePulses.forEach((c, i) => {
        const pulse = 4 + Math.sin(t0 * 0.002 + i * 1.5) * 2;
        c.setAttribute('r', pulse.toFixed(1));
        const opacity = 0.6 + Math.sin(t0 * 0.002 + i * 1.5) * 0.4;
        c.setAttribute('opacity', opacity.toFixed(2));
      });

      if (!reduceMotion) {
        t0 += 16;
        animId = requestAnimationFrame(render);
      }
    }

    render();

    return () => {
      if (animId) cancelAnimationFrame(animId);
      dots.forEach(d => {
        if (d.el.parentNode) d.el.parentNode.removeChild(d.el);
        if (d.halo.parentNode) d.halo.parentNode.removeChild(d.halo);
      });
      nodePulses.forEach(c => {
        if (c.parentNode) c.parentNode.removeChild(c);
      });
    };
  }, []);

  return (
    <svg ref={svgRef} className="process-svg" viewBox="0 0 800 500" aria-hidden="true">
      {/* Upper loop - animated stroke */}
      <path
        className="process-loop-path"
        d="M 400 60 C 250 60 250 240 400 240 C 550 240 550 60 400 60 Z"
        fill="none"
        stroke="rgba(155,123,255,0.25)"
        strokeWidth="1.5"
      />
      {/* Upper loop glow */}
      <path
        className="process-loop-glow"
        d="M 400 60 C 250 60 250 240 400 240 C 550 240 550 60 400 60 Z"
        fill="none"
        stroke="rgba(155,123,255,0.08)"
        strokeWidth="6"
      />
      {/* Lower loop - animated stroke */}
      <path
        className="process-loop-path"
        d="M 400 260 C 250 260 250 440 400 440 C 550 440 550 260 400 260 Z"
        fill="none"
        stroke="rgba(155,123,255,0.25)"
        strokeWidth="1.5"
      />
      {/* Lower loop glow */}
      <path
        className="process-loop-glow"
        d="M 400 260 C 250 260 250 440 400 440 C 550 440 550 260 400 260 Z"
        fill="none"
        stroke="rgba(155,123,255,0.08)"
        strokeWidth="6"
      />
    </svg>
  );
}

function Process() {
  return (
    <section className="sec" id="process">
      <div className="wrap">
        <ScrollReveal>
          <div className="sec-head">
            <div className="eyebrow">HOW WE WORK</div>
            <h2>A loop, not a line.</h2>
            <p>
              Business needs don't stop changing once a project ships — so our process doesn't
              either. Every build feeds back into the next round of adapting.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="process-wrap">
            <ProcessLoopSVG />

            {steps.map((step, i) => (
              <div
                key={i}
                className="process-node"
                style={step.position}
              >
                <span className="tag">{step.tag}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Mobile fallback */}
        <div className="process-mobile">
          {steps.map((step, i) => (
            <ScrollReveal key={i} delay={i * 0.1}>
              <div className="step">
                <div className="dot" />
                <div>
                  <span className="tag">{step.tag}</span>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Process;
